import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  useGetAllInvoiceQuery,
  useCreateEmailInvoiceMutation,
} from "../../../Api/attoDeskApi";
import { useStyles } from "../../../theme/appStyles";
import { useNavigate } from "react-router";
import {
  ICompanyDetails,
  ICustomer,
  ICustomerProject,
} from "../../../Api/Interface/api.interface";
import dayjs, { Dayjs } from "dayjs";
import {
  ProductType,
  TransferType,
  WorkOrderStatus,
} from "../../../Core/Enum/enum";
import { useParams } from "react-router-dom";
import ReactToPrint from "react-to-print";
import LocalPrintshopSharpIcon from "@mui/icons-material/LocalPrintshopSharp";
import EmailIcon from "@mui/icons-material/Email";
import { useNotifier } from "../../../Core/Notifier";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import moment from "moment";
import axios from "axios";

// ─── Interfaces ──────────────────────────────────────────────────────────────
interface IProduct {
  productName: string;
  productDescription: string;
  productImg: String | null;
  numberOfPassengers: number | null;
  transferType: string;
  transferTypeFrom: string;
  transferTypeTo: string;
  transferTypeDescription: string;
  departureDateTime: Dayjs | null;
  returnDateTime: Dayjs | null;
  departureAddress: string;
  destinationAddress: string;
  multiDestination: string;
  quantity: number | null;
  price: number | null;
  tax: number | null;
  taxAmount: number | null;
  total: number;
  taxId: number | null;
  unitPriceWithDiscount: number | null;
  unitTotalWithDiscount: number;
  productType: string;
  productLongDescription: string;
  productShortDescription: string;
  pickups?: { address: string; city: string; dateTime: dayjs.Dayjs | null }[];
  dropoffs?: { address: string; city: string; dateTime: dayjs.Dayjs | null }[];
  returnPickups?: { address: string; city: string; dateTime: dayjs.Dayjs | null }[];
  returnDropoffs?: { address: string; city: string; dateTime: dayjs.Dayjs | null }[];
}

interface IDiscount {
  value: number | null;
  description: string;
}

interface IQuoteWorkOrder {
  id: number;
  quoteWorkOrderCompanyId: number;
  quoteWorkOrderCustomerId: string;
  quoteWorkOrderCustomerProjectId: string;
  quoteWorkOrderNo: string;
  quoteWorkOrderIssueDate: Dayjs;
  quoteWorkOrderDueDate: Dayjs;
  quoteTripId: string;
  quoteTripName: string;
  quoteWorkOrderAmount: number | null;
  quoteWorkOrderStatus: number;
  quoteWorkOrderSale: IProduct[];
  quoteWorkOrderDiscount: string;
  quoteWorkOrderSubTotal: string;
  quoteWorkOrderSaleGrandTotal: string;
  quoteWorkOrderSaleDiscountDescription: IDiscount[];
  customer: ICustomer;
  company: ICompanyDetails;
  project: ICustomerProject;
  quoteWorkOrderCustomerRemark: string;
  quoteWorkOrderCustomerMemo: string;
  quoteWorkOrderSaleDiscount: string;
  quoteWorkOrderSaleSubTotal: string;
  created_by: number;
  created_at: string;
  mainStatus: number;
  invoiceId: number;
  InvoiceNo: string;
  amount_paid: number;
  po_number: string;
  work_order_id: string;
}

interface Payment {
  id: number;
  transaction_id: string;
  amount: number;
  payment_date: string;
  payment_method: string;
  payment_mode: string;
  description: string;
  file_path?: string;
  created_at: string;
  updated_at: string;
  date: string;
  is_return: number;
  is_deleted: number;
}

interface ViewInvoiceProps {
  invoiceId: string;
  type: string;
}

// ─── Component ───────────────────────────────────────────────────────────────
const ViewInvoice: React.FC<ViewInvoiceProps> = ({ invoiceId, type }) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const theme = useTheme();
  const { id: InvoiceId } = useParams<{ id: string }>();
  useEffect(() => {}, [InvoiceId]);
  const componentRef = useRef<HTMLDivElement>(null);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [newEmailInvoice, { isLoading: isEmailSending }] = useCreateEmailInvoiceMutation();
  const { showErrorMessage, showMessage } = useNotifier();

  const [dataLoaded, setDataLoaded] = useState(false);
  const LARAVEL_API_URL = process.env.REACT_APP_LARAVEL_API;
  const [totalPrice, setTotalPrice] = useState(0);
  const [totalTax, setTotalTax] = useState(0);
  const [payments, setPayments] = useState<Payment[]>([]);
  const API_URL = process.env.REACT_APP_API_URL;

  const {
    data: workOrderData,
    isLoading: workIsLoading,
    isError: workOrderError,
    refetch: workOrderRefetch,
  } = useGetAllInvoiceQuery({
    searchText: "",
    id: String(invoiceId),
    quoteWorkOrderCustomerId: "",
    quoteWorkOrderCustomerProjectId: "",
  });

  const WorkOrderDataList = useMemo(() => {
    if (workOrderData && Array.isArray(workOrderData.data) && workOrderData.data.length > 0) {
      return workOrderData.data[0] as IQuoteWorkOrder;
    }
    return null;
  }, [workOrderData]);

  const coursings: IProduct[] = Array.isArray(WorkOrderDataList?.quoteWorkOrderSale)
    ? WorkOrderDataList!.quoteWorkOrderSale
    : [];

  const discountList: IDiscount[] = Array.isArray(WorkOrderDataList?.quoteWorkOrderSaleDiscountDescription)
    ? WorkOrderDataList!.quoteWorkOrderSaleDiscountDescription
    : [];

  useEffect(() => {
    if (WorkOrderDataList && WorkOrderDataList.project) {
      setDataLoaded(true);
    } else {
      setDataLoaded(false);
    }
  }, [workOrderData]);

  useEffect(() => {
    fetchPayments();
  }, [WorkOrderDataList?.work_order_id]);

  const fetchPayments = () => {
    if (!WorkOrderDataList?.work_order_id) return;
    axios.get(`${API_URL}/payments-by-workorder?work_order_id=${WorkOrderDataList?.work_order_id}`)
      .then(response => {
        const sortedPayments = response.data.sort((a: Payment, b: Payment) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
        const validPayments = sortedPayments.filter(
          (p: Payment) => p.is_deleted === 0 && p.is_return === 0
        );
        setPayments(validPayments);
      })
      .catch(error => console.error("Error fetching payments:", error));
  };

  useEffect(() => {
    let newTotalPrice = 0;
    let newTotalTax = 0;
    coursings.forEach((product) => {
      newTotalPrice += product.price ?? 0;
      newTotalTax += product.taxAmount ?? 0;
    });
    setTotalPrice(newTotalPrice);
    setTotalTax(newTotalTax);
  }, [coursings]);

  const handleSendEmail = async () => {
    try {
      if (!componentRef.current) { showErrorMessage("The invoice component is not available."); return; }
      if (!dataLoaded) { showErrorMessage("Data is still loading. Please wait."); return; }
      if (!WorkOrderDataList || !WorkOrderDataList.project) { showErrorMessage("Required data is missing."); return; }
      const pdf = new jsPDF();
      const canvas = await html2canvas(componentRef.current);
      const imgData = canvas.toDataURL("image/png");
      const imgWidth = pdf.internal.pageSize.getWidth();
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight);
      const pdfBlob = pdf.output("blob");
      const file = new File([pdfBlob], `${WorkOrderDataList?.InvoiceNo}.pdf`, { type: "application/pdf" });
      const formData = new FormData();
      formData.append("customer_id", WorkOrderDataList?.project.customerId.toString() || "");
      formData.append("project_id", WorkOrderDataList?.project.id.toString() || "");
      formData.append("to", WorkOrderDataList?.project.projectEmail || "");
      formData.append("subject", "Invoice for Your Project");
      formData.append("message", "Please find attached the invoice for your project.");
      formData.append("invoice", file);
      const emailResponse = await newEmailInvoice(formData).unwrap();
      if (!emailResponse) { showErrorMessage("Failed to send email"); }
      else { showMessage("Email sent successfully"); }
    } catch (error) {
      console.error("Error sending email:", error);
    }
  };

  // ─── Style constants ──────────────────────────────────────────────────────
  const BORDER     = "1px solid #999";
  const FONT_SM    = "0.82rem";
  const FONT_MD    = "0.9rem";

  // Phone formatter
  const formatPhone = (num: string): string => {
    const digits = num.replace(/\D/g, "");
    if (digits.length === 10) return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
    if (digits.length === 11) return `${digits.slice(0, 1)}-${digits.slice(1, 4)}-${digits.slice(4, 7)}-${digits.slice(7)}`;
    return num;
  };

  const primaryPhone   = formatPhone(WorkOrderDataList?.company?.companyMobileNumber || "");
  const alternatePhone = formatPhone((WorkOrderDataList?.company as any)?.companyAltMobileNumber || "");
  const phoneString    = primaryPhone && alternatePhone
    ? `${primaryPhone} / ${alternatePhone}`
    : primaryPhone || alternatePhone;

  const isProject = !!WorkOrderDataList?.quoteWorkOrderCustomerProjectId;

  // ─── Build pickup / dropoff summaries from all products ──────────────────
  const allPickups: string[] = [];
  const allDropoffs: string[] = [];

  coursings.forEach(product => {
    (product.pickups || []).filter(p => p.address || p.city).forEach((p, i, arr) => {
      const addr = [p.address].filter(Boolean).join(", ");
      const label = arr.length > 1 ? `${i + 1}${i === 0 ? "st" : i === 1 ? "nd" : i === 2 ? "rd" : "th"} Pickup` : "Pickup";
      allPickups.push(`${label} – ${addr}`);
    });
    (product.dropoffs || []).filter(d => d.address || d.city).forEach((d, i, arr) => {
      const addr = [d.address].filter(Boolean).join(", ");
      allDropoffs.push(addr);
    });
    // fallback
    if (!(product.pickups || []).length && product.departureAddress) {
      allPickups.push(product.departureAddress);
    }
    if (!(product.dropoffs || []).length && product.destinationAddress) {
      allDropoffs.push(product.destinationAddress);
    }
  });

  // ─── Start / Return time ──────────────────────────────────────────────────
  let startTime = "";
  let returnTime = "";

  for (const p of coursings) {
    if (!startTime) {
      if (p.departureDateTime && dayjs(p.departureDateTime).isValid()) {
        startTime = dayjs(p.departureDateTime).format("h A");
      } else {
        const fp = (p.pickups || []).find(r => r.dateTime && dayjs(r.dateTime).isValid());
        if (fp?.dateTime) startTime = dayjs(fp.dateTime).format("h A");
      }
    }
  }

  const allTimes: dayjs.Dayjs[] = [];
  for (const p of coursings) {
    [...(p.pickups || []), ...(p.dropoffs || []), ...(p.returnPickups || []), ...(p.returnDropoffs || [])]
      .forEach(r => { if (r.dateTime && dayjs(r.dateTime).isValid()) allTimes.push(dayjs(r.dateTime)); });
    if (p.returnDateTime && dayjs(p.returnDateTime).isValid()) allTimes.push(dayjs(p.returnDateTime));
  }
  if (allTimes.length > 0) {
    allTimes.sort((a, b) => b.valueOf() - a.valueOf());
    returnTime = allTimes[0].format("h:mm A");
  }

  // Balance
  const grandTotal   = parseFloat(WorkOrderDataList?.quoteWorkOrderSaleGrandTotal || "0");
  const amountPaid   = parseFloat(String(WorkOrderDataList?.amount_paid || "0"));
  const balanceAmount = Math.max(0, grandTotal - amountPaid);

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div>
      <style>{`
        @media print {
          * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
        }
      `}</style>

      <Grid container spacing={2}>
        {/* Toolbar */}
        <Grid item lg={12} md={12} sm={12} xs={12}>
          <Card className={classes.quotesCardInvoice}>
            <Grid container spacing={1} padding="2px">
              <Grid item lg={12} md={12} sm={12} xs={12} sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Grid container spacing={1} justifyContent="flex-end" sx={{ display: "flex" }} />
              </Grid>
            </Grid>
          </Card>
        </Grid>

        {/* ── PRINTABLE DOCUMENT ──────────────────────────────────────────── */}
        <Grid item lg={12} md={12} sm={12} xs={12}>
          <div style={{ overflowX: "auto", width: "100%" }}>
            <div ref={componentRef} style={{ minWidth: "700px" }}>
              <Card style={{
                position: "relative",
                boxSizing: "border-box",
                backgroundColor: theme.palette.cardAdd?.bgColor || "#fff",
                boxShadow: "none",
                width: "100%",
                margin: "0 auto",
              }}>

                {/* ══ HEADER ══════════════════════════════════════════════
                    Left:  Logo
                    Right: Company name (bold large) + address + phone + email/web
                    Below right: Invoice # and Date
                ══════════════════════════════════════════════════════════ */}
                <div style={{ padding: "20px 24px 12px 24px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>

                    {/* Left — Logo */}
                    <div style={{ flex: "0 0 auto" }}>
                      <img
                        src={WorkOrderDataList?.company?.CompanyLogo
                          ? `${LARAVEL_API_URL}/${WorkOrderDataList.company.CompanyLogo}` : ""}
                        alt="Company Logo"
                        className={classes.invoiceImg}
                        style={{ 
                          // maxHeight: "100px",
                           maxWidth: "440px", objectFit: "contain", display: "block" }}
                      />
                    </div>

                    {/* Right — Company details + INVOICE title */}
                    <div style={{ textAlign: "right", flex: "0 0 auto", maxWidth: "55%" }}>
                      {/* INVOICE title — italic bold large, top-right */}
                      <div style={{
                        fontFamily: "Georgia, serif",
                        fontSize: "1.5rem",
                        fontWeight: "bold",
                        fontStyle: "italic",
                        marginBottom: "8px",
                        letterSpacing: "1px",
                      }}>
                        INVOICE
                      </div>

                      {/* Company name bold */}
                      {/* <Typography style={{ fontWeight: "bold", fontSize: "1rem", lineHeight: 1.4 }}>
                        {WorkOrderDataList?.company?.companyName || ""}
                      </Typography> */}

                      {/* Address lines */}
                      <Typography style={{ fontSize: FONT_MD, fontWeight: "bold", lineHeight: 1.5, textAlign:"left" }}>
                        {[
                          WorkOrderDataList?.company?.companyStreetNumber,
                          WorkOrderDataList?.company?.companyStreetName,
                        ].filter(Boolean).join(" ")}
                      </Typography>
                      <Typography style={{ fontSize: FONT_MD, fontWeight: "bold", lineHeight: 1.5,  textAlign:"left"  }}>
                        {[
                          WorkOrderDataList?.company?.companyCity,
                          WorkOrderDataList?.company?.companyProvince,
                        ].filter(Boolean).join(", ")}
                        {WorkOrderDataList?.company?.companyPostalCode
                          ? ` ${WorkOrderDataList.company.companyPostalCode}` : ""}
                      </Typography>

                      {/* Phone bold */}
                      {phoneString && (
                        <Typography style={{ fontSize: FONT_MD, fontWeight: "bold", lineHeight: 1.5,  textAlign:"left"  }}>
                          Phone: {phoneString}
                        </Typography>
                      )}

                      {/* Email + website — blue underline */}
                      <div style={{ display: "flex", justifyContent: "flex-start", gap: "16px", marginTop: "2px" }}>
                        {WorkOrderDataList?.company?.companyEmail && (
                          <Typography style={{ fontSize: FONT_MD, color: "#1155CC", textDecoration: "underline", fontWeight: "bold",  textAlign:"left"  }}>
                            {WorkOrderDataList.company.companyEmail}
                          </Typography>
                        )}
                        {(WorkOrderDataList?.company as any)?.companyWebSiteLink && (
                          <Typography style={{ fontSize: FONT_MD, 
                          // color: "#1155CC", 
                          // textDecoration: "underline",
                           fontWeight: "bold" }}>
                            {(WorkOrderDataList?.company as any).companyWebSiteLink}
                          </Typography>
                        )}
                      </div>

                      {/* Invoice # and Date — bold, below company info */}
                      <div style={{ marginTop: "10px", textAlign: "left" }}>
                        <Typography style={{ fontWeight: "bold", fontSize: FONT_MD }}>
                          Invoice #{WorkOrderDataList?.InvoiceNo || ""}
                        </Typography>
                        <Typography style={{ fontWeight: "bold", fontSize: FONT_MD }}>
                          Invoice{" "}
                          {WorkOrderDataList?.quoteWorkOrderIssueDate
                            ? dayjs(WorkOrderDataList.quoteWorkOrderIssueDate).format("MMMM D/YYYY")
                            : ""}
                        </Typography>
                        {WorkOrderDataList?.po_number && (
                          <Typography style={{ fontWeight: "bold", fontSize: FONT_MD }}>
                            PO #{WorkOrderDataList.po_number}
                          </Typography>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                {/* ══ END HEADER ══════════════════════════════════════════ */}

                {/* ══ BODY ════════════════════════════════════════════════ */}
                <div style={{ padding: "0 24px 20px 24px" }}>

                  {/* Company Name: bold label + value */}
                  <div style={{ marginBottom: "16px" }}>
                    <Typography style={{ fontWeight: "bold", fontSize: "1.05rem" }}>
                      <strong>Company Name:</strong>{" "}
                      {isProject
                        ? WorkOrderDataList?.project?.projectName || ""
                        : `${WorkOrderDataList?.customer?.customerFirstName || ""} ${WorkOrderDataList?.customer?.customerLastName || ""}`.trim()}
                    </Typography>
                  </div>

                  {/* PICK UP section */}
                  {allPickups.length > 0 && (
                    <div style={{ marginBottom: "12px" }}>
                      <Typography style={{ fontWeight: "bold", fontSize: "1rem" }}>
                        <strong>PICK UP:</strong>
                      </Typography>
                      {allPickups.map((addr, i) => (
                        <Typography key={i} style={{ fontSize: FONT_MD, marginLeft: i === 0 ? "80px" : "80px", marginTop: i === 0 ? "-22px" : "0" }}>
                          {addr}
                        </Typography>
                      ))}
                    </div>
                  )}

                  {/* DROP OFF section */}
                  {allDropoffs.length > 0 && (
                    <div style={{ marginBottom: "16px" }}>
                      <Typography style={{ fontWeight: "bold", fontSize: "1rem" }}>
                        <strong>DROP OFF: </strong>
                      </Typography>
                      {allDropoffs.map((addr, i) => (
                        <Typography key={i} style={{ fontSize: FONT_MD, marginLeft: i === 0 ? "88px" : "88px", marginTop: i === 0 ? "-22px" : "0" }}>
                          {addr}
                        </Typography>
                      ))}
                    </div>
                  )}

                  {/* Start time / Return time line */}
                  {(startTime || returnTime) && (
                    <div style={{ display: "flex", gap: "60px", marginBottom: "16px" }}>
                      {startTime && (
                        <Typography style={{ fontWeight: "bold", fontSize: FONT_MD }}>
                          <strong>Start time:</strong> {startTime}
                        </Typography>
                      )}
                      {returnTime && (
                        <Typography style={{ fontWeight: "bold", fontSize: FONT_MD }}>
                          <strong>Return Time</strong> {returnTime}
                        </Typography>
                      )}
                    </div>
                  )}

                 {/* ── Description / Charges Table ── */}
<table style={{ width: "100%", borderCollapse: "collapse", marginBottom: "0" }}>
  <colgroup>
    <col style={{ width: "75%" }} />
    <col style={{ width: "6%" }} />   {/* $ column */}
    <col style={{ width: "19%" }} />  {/* amount column */}
  </colgroup>
  <thead>
    <tr>
      <th style={{
        border: BORDER, padding: "10px 12px",
        textAlign: "center", fontWeight: "bold",
        fontSize: FONT_MD, backgroundColor: "#f5f5f5",
      }}>
        Description
      </th>
      <th colSpan={2} style={{
        border: BORDER, padding: "10px 12px",
        textAlign: "center", fontWeight: "bold",
        fontSize: FONT_MD, backgroundColor: "#f5f5f5",
      }}>
        Charges
      </th>
    </tr>
  </thead>
  <tbody>
    {workOrderError || coursings.length === 0 ? (
      <tr>
        <td colSpan={3} style={{ border: BORDER, padding: "10px 12px", textAlign: "center" }}>
          {workOrderError ? "Error fetching data" : "No data available"}
        </td>
      </tr>
    ) : (
      coursings
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
        .map((product, index) => (
          <tr key={index}>
            <td style={{ border: BORDER, padding: "10px 12px", fontSize: FONT_MD, textAlign: "center" }}>
              <strong>{product.productName}</strong>
              {product.numberOfPassengers ? ` — ${product.numberOfPassengers} Passengers` : ""}
              {product.productDescription && (
                <div style={{ fontSize: FONT_SM, marginTop: "4px" }}>{product.productDescription}</div>
              )}
              {product.productLongDescription && (
                <div style={{ fontSize: FONT_SM, marginTop: "2px" }}>{product.productLongDescription}</div>
              )}
            </td>
            {/* $ and amount share the same column alignment as totals below */}
            <td style={{
              border: BORDER, padding: "10px 6px",
              fontSize: FONT_MD, textAlign: "right",
              verticalAlign: "top", borderRight: "none",   // ← no border between $ and amount
            }}>
              {(product.price ?? 0) > 0 ? "$" : ""}
            </td>
            <td style={{
              border: BORDER, padding: "10px 12px",
              fontSize: FONT_MD, textAlign: "right",
              verticalAlign: "top", borderLeft: "none",    // ← no border between $ and amount
            }}>
              {(product.price ?? 0) > 0
                ? (product.price ?? 0).toLocaleString("en-CA", { minimumFractionDigits: 2 })
                : ""}
            </td>
          </tr>
        ))
    )}

    {/* Empty filler rows */}
    {coursings.length < 6 && Array.from({ length: Math.max(0, 6 - coursings.length) }).map((_, i) => (
      <tr key={`empty-${i}`}>
        <td style={{ border: BORDER, padding: "10px 12px", height: "36px" }}></td>
        <td style={{ border: BORDER, padding: "10px 6px", borderRight: "none" }}></td>
        <td style={{ border: BORDER, padding: "10px 12px", borderLeft: "none" }}></td>
      </tr>
    ))}
  </tbody>
</table>

{/* ── Totals table — same 3-col structure, $ col has no internal border ── */}
<table style={{ width: "100%", borderCollapse: "collapse" }}>
  <colgroup>
    <col style={{ width: "75%" }} />
    <col style={{ width: "6%" }} />
    <col style={{ width: "19%" }} />
  </colgroup>
  <tbody>
    {discountList.map((discount, index) => (
      <tr key={index}>
        <td style={{ padding: "4px 12px", border: BORDER, textAlign: "right", fontSize: FONT_MD }}>
          {discount.description || "Discount"}
        </td>
        <td style={{ border: BORDER, padding: "4px 6px", textAlign: "right", fontSize: FONT_MD, borderRight: "none" }}>$</td>
        <td style={{ border: BORDER, padding: "4px 12px", textAlign: "right", fontSize: FONT_MD, borderLeft: "none" }}>
          {(discount.value ?? 0).toFixed(2)}
        </td>
      </tr>
    ))}

    <tr>
      <td style={{ padding: "6px 12px", border: BORDER, textAlign: "right", fontWeight: "bold", fontSize: FONT_MD }}>AMOUNT</td>
      <td style={{ border: BORDER, padding: "6px 6px", textAlign: "right", fontWeight: "bold", fontSize: FONT_MD, borderRight: "none" }}>$</td>
      <td style={{ border: BORDER, padding: "6px 12px", textAlign: "right", fontWeight: "bold", fontSize: FONT_MD, borderLeft: "none" }}>
        {totalPrice.toLocaleString("en-CA", { minimumFractionDigits: 2 })}
      </td>
    </tr>
    <tr>
      <td style={{ padding: "6px 12px", border: BORDER, textAlign: "right", fontWeight: "bold", fontSize: FONT_MD }}>HST TAX</td>
      <td style={{ border: BORDER, padding: "6px 6px", textAlign: "right", fontWeight: "bold", fontSize: FONT_MD, borderRight: "none" }}>$</td>
      <td style={{ border: BORDER, padding: "6px 12px", textAlign: "right", fontWeight: "bold", fontSize: FONT_MD, borderLeft: "none" }}>
        {totalTax.toLocaleString("en-CA", { minimumFractionDigits: 2 })}
      </td>
    </tr>
    <tr>
      <td style={{ padding: "6px 12px", border: BORDER, textAlign: "right", fontWeight: "bold", fontSize: FONT_MD }}>TOTAL</td>
      <td style={{ border: BORDER, padding: "6px 6px", textAlign: "right", fontWeight: "bold", fontSize: FONT_MD, borderRight: "none" }}>$</td>
      <td style={{ border: BORDER, padding: "6px 12px", textAlign: "right", fontWeight: "bold", fontSize: FONT_MD, borderLeft: "none" }}>
        {grandTotal.toLocaleString("en-CA", { minimumFractionDigits: 2 })}
      </td>
    </tr>

    {payments.length > 0 && payments.map((payment) => (
      <tr key={payment.id}>
        <td style={{ padding: "4px 12px", border: BORDER, textAlign: "right", fontSize: FONT_SM }}>
          {moment(payment.payment_date).format("DD-MMM-YY")} ({payment.payment_method})
        </td>
        <td style={{ border: BORDER, padding: "4px 6px", textAlign: "right", fontSize: FONT_SM, borderRight: "none" }}>$</td>
        <td style={{ border: BORDER, padding: "4px 12px", textAlign: "right", fontSize: FONT_SM, borderLeft: "none" }}>
          {Number(payment.amount).toFixed(2)}
        </td>
      </tr>
    ))}

    {/* {balanceAmount > 0 && (
      <tr>
        <td style={{ padding: "6px 12px", border: BORDER, textAlign: "right", fontWeight: "bold", fontSize: FONT_MD }}>Balance Amount</td>
        <td style={{ border: BORDER, padding: "6px 6px", textAlign: "right", fontWeight: "bold", fontSize: FONT_MD, borderRight: "none" }}>$</td>
        <td style={{ border: BORDER, padding: "6px 12px", textAlign: "right", fontWeight: "bold", fontSize: FONT_MD, borderLeft: "none" }}>
          {balanceAmount.toLocaleString("en-CA", { minimumFractionDigits: 2 })}
        </td>
      </tr>
    )} */}
  </tbody>
</table>

                

                  {/* HST Number */}
                  {WorkOrderDataList?.company?.companyHstNo && (
                    <Typography style={{ marginTop: "14px", fontSize: FONT_MD, textAlign:'center' }}>
                      HST:{WorkOrderDataList.company.companyHstNo}
                    </Typography>
                  )}

                  {/* Comments / Memo */}
                  <div style={{ marginTop: "16px" }}>
                    <Typography style={{ fontWeight: "bold", fontSize: FONT_MD }}>
                      Comments:
                    </Typography>
                    {WorkOrderDataList?.quoteWorkOrderCustomerMemo?.trim() && (
                      <Typography style={{ fontSize: FONT_MD, marginTop: "4px" }}>
                        {WorkOrderDataList.quoteWorkOrderCustomerMemo}
                      </Typography>
                    )}
                  </div>

                  {/* Terms & conditions */}
                  {WorkOrderDataList?.company?.companyTermsAndConditions?.trim() && (
                    <div style={{ marginTop: "10px" }}>
                      {WorkOrderDataList.company.companyTermsAndConditions
                        .split("\n")
                        .filter(line => line.trim() !== "")
                        .map((line, i) => (
                          <Typography key={i} variant="body2" style={{ fontSize: FONT_SM }}>
                            {line}
                          </Typography>
                        ))}
                    </div>
                  )}

                  {/* Dual signature line — matches PDF exactly */}
                  <div style={{ marginTop: "28px", display: "flex", justifyContent: "space-between" }}>
                    <div>
                      <Typography style={{ fontSize: FONT_SM }}>
                        ………………………………………………………
                      </Typography>
                      <Typography style={{ fontWeight: "bold", fontSize: FONT_SM }}>
                        Customers Signature
                      </Typography>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <Typography style={{ fontSize: FONT_SM }}>
                        ………………………………………………………
                      </Typography>
                      <Typography style={{ fontWeight: "bold", fontSize: FONT_SM, textAlign:'left' }}>
                        Driver/Operator Name
                      </Typography>
                    </div>
                  </div>

                  {/* Cancellation Policy — bold uppercase */}
                  {WorkOrderDataList?.company?.companyCancellationPolicy?.trim() && (
                    <Typography style={{
                      marginTop: "16px",
                      fontSize: FONT_SM,
                      fontWeight: "bold",
                      textTransform: "uppercase",
                      lineHeight: 1.5,
                    }}>
                      {WorkOrderDataList.company.companyCancellationPolicy}
                    </Typography>
                  )}

                  {/* Watermark */}
                  {WorkOrderDataList?.company?.CompanyWatermark && (
                    <div style={{
                      position: "absolute", bottom: 0, left: 0,
                      width: "100%", height: "100%",
                      display: "flex", justifyContent: "flex-start", alignItems: "flex-end",
                      zIndex: 0, pointerEvents: "none", overflow: "hidden",
                    }}>
                      <img
                        src={`${LARAVEL_API_URL}/${WorkOrderDataList.company.CompanyWatermark}`}
                        alt="Watermark"
                        style={{ maxWidth: "60%",
                          //  opacity: 0.12
                           }}
                      />
                    </div>
                  )}

                </div>{/* end body */}

                {/* ══ FOOTER ══════════════════════════════════════════════ */}
                {WorkOrderDataList?.company?.companyFooterDetails && (
                  <div style={{ padding: "12px 24px 16px 24px", borderTop: "1px solid #ddd" }}>
                    <Typography variant="body2" align="center" style={{ fontSize: FONT_SM }}>
                      {WorkOrderDataList.company.companyFooterDetails.split("\n").map((line, i) => (
                        <span key={i}>{line}<br /></span>
                      ))}
                    </Typography>
                  </div>
                )}

              </Card>
            </div>
          </div>
        </Grid>

        {/* Print button */}
        {type !== "email" && (
          <Grid item lg={12} md={12} sm={12} xs={12} padding="10px">
            <Grid container>
              <Grid item lg={12} md={12} sm={12} xs={12} sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Grid container justifyContent="flex-end" sx={{ display: "flex" }}>
                  <Grid item>
                    <ReactToPrint
                      trigger={() => (
                        <Button variant="contained" startIcon={<LocalPrintshopSharpIcon />} className={classes.addButton}>
                          Print
                        </Button>
                      )}
                      content={() => componentRef.current}
                      documentTitle={WorkOrderDataList?.InvoiceNo || "Invoice"}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        )}

      </Grid>
    </div>
  );
};

export default ViewInvoice;
